import { decamelizeKeys } from 'humps';

import {
  AgentExecution as AgentExecutionApi,
  AgentExecutionMessage as AgentExecutionMessageApi,
  MessageContext as MessageContextApi,
} from '~/api/sylvanus/typedefs.autogen';
import { SearchQuery } from '~/features/advanced-search/types';
import {
  AgentExecution,
  AgentMessage,
  CamelizedServerMessage,
  MessageContext,
} from '~/features/ask-anything/agentic-ai/types';
import { SearchEntity } from '~/features/ask-anything/types/chatbot';

import {
  mapCitations,
  mapFeedbackDetailsFromUserRating,
} from '../../transformers/conversationalAi';

export const mapServerAgentExecutionToAgentExecution = (
  agentExecution: AgentExecutionApi,
): AgentExecution => {
  return {
    id: agentExecution.id,
    createdAt: new Date(agentExecution.datetime_created),
    creator: {
      id: agentExecution.created_by_user_id,
      email: agentExecution.created_by_user_email,
    },
    agentType: agentExecution.agent_type,
    agentConfiguration: {
      externalEntities:
        agentExecution.agent_configuration.external_entities ?? undefined,
      metadata: agentExecution.agent_configuration.metadata ?? {},
    },
    messages: agentExecution.messages.map(
      mapServerAgentExecutionMessageToAgentExecutionMessage,
    ),
  };
};

export const mapServerAgentExecutionMessageToAgentExecutionMessage = (
  agentExecutionMessage: AgentExecutionMessageApi,
): AgentMessage => {
  const mappedCitations = mapCitations(agentExecutionMessage);

  const rawMessage = agentExecutionMessage as any;
  const additionalInfo =
    rawMessage.additional_info ?? rawMessage.additionalInfo ?? undefined;

  const userType: 'current' | 'other' =
    agentExecutionMessage.role === 'user' ? 'current' : 'other';

  const result: AgentMessage = {
    id: agentExecutionMessage.id,
    agentExecutionId: agentExecutionMessage.ae_id,
    userType,
    text: agentExecutionMessage.message_text ?? '',
    messageContext: agentExecutionMessage.message_context
      ? mapServerMessageContextToMessageContext(
          agentExecutionMessage.message_context,
        )
      : null,
    timestamp: new Date(agentExecutionMessage.datetime_created),
    responseSuggestions: agentExecutionMessage.response_suggestions,
    sources: [],
    citations: Object.values(mappedCitations),
    feedbackDetails: agentExecutionMessage.user_rating
      ? {
          ...mapFeedbackDetailsFromUserRating(
            agentExecutionMessage.user_rating,
          ),
          comment: agentExecutionMessage.user_comment ?? undefined,
        }
      : undefined,
    additionalInfo: additionalInfo,
  };

  return result;
};

/**
 * Transforms a camelized stream event message (from SSE camelizeKeys) into
 * an AgentMessage. Centralizes the decamelize round-trip so callers don't
 * need to inline `decamelizeKeys(msg) as ServerAgentExecutionMessage`.
 */
export const transformCamelizedStreamMessage = (
  camelizedMessage: CamelizedServerMessage,
): AgentMessage =>
  mapServerAgentExecutionMessageToAgentExecutionMessage(
    decamelizeKeys(camelizedMessage) as unknown as AgentExecutionMessageApi,
  );

const mapServerMessageContextToMessageContext = (
  messageContext: MessageContextApi,
): MessageContext => {
  return {
    searchQuery: messageContext.search_query as SearchQuery,
    searchEntity: messageContext.search_entity as SearchEntity,
    selectedIds: messageContext.selected_ids,
    entitiesInView: messageContext.entities_in_view,
    messageType: messageContext.message_type ?? undefined,
  };
};
